import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import MenuBar from "@/components/MenuBar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Mic, MicOff, Send, Calendar } from "lucide-react";
import { AMAService } from "@/services/amaService";
import { AMAQuestion } from "@/types/ama";
import { format } from "date-fns";
import { nb, enUS } from "date-fns/locale";

const AMAAnswerPage = () => {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const locale = i18n.language === 'no' ? nb : enUS;

  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isRecording, setIsRecording] = useState<string | null>(null);
  const [recognition, setRecognition] = useState<any>(null);
  const currentQuestionIdRef = useRef<string | null>(null);

  // Fetch AMA by token
  const { data: amas } = useQuery({
    queryKey: ['amas'],
    queryFn: () => AMAService.getAllAMAs(),
  });

  const ama = amas?.find(a => a.expert_token === token);

  const { data: questions } = useQuery({
    queryKey: ['ama-questions', ama?.id],
    queryFn: () => AMAService.getQuestionsByAMA(ama!.id),
    enabled: !!ama?.id,
  });

  const { data: existingAnswers } = useQuery({
    queryKey: ['ama-answers', ama?.id],
    queryFn: () => AMAService.getAnswersByAMA(ama!.id),
    enabled: !!ama?.id,
  });

  const publishAnswerMutation = useMutation({
    mutationFn: ({ questionId, answerText }: { questionId: string; answerText: string }) =>
      AMAService.createAnswer({
        question_id: questionId,
        ama_id: ama!.id,
        answer_text: answerText,
      }),
    onSuccess: () => {
      toast.success(t('ama.expert.answerPublished'));
      queryClient.invalidateQueries({ queryKey: ['ama-answers', ama?.id] });
    },
    onError: () => {
      toast.error(t('ama.expert.answerError'));
    },
  });

  // Initialize Web Speech API
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = false; // Changed to false for better control
      recognitionInstance.interimResults = false; // Changed to false to avoid duplicates
      recognitionInstance.lang = i18n.language === 'no' ? 'nb-NO' : 'en-US';
      
      console.log('Speech recognition initialized with lang:', recognitionInstance.lang);
      
      recognitionInstance.onstart = () => {
        console.log('Speech recognition started');
      };
      
      recognitionInstance.onresult = (event: any) => {
        console.log('Speech recognition result received:', event);
        
        // Get only the final transcript
        const results = event.results;
        let finalTranscript = '';
        
        for (let i = 0; i < results.length; i++) {
          if (results[i].isFinal) {
            finalTranscript += results[i][0].transcript;
          }
        }
        
        console.log('Final transcript:', finalTranscript);
        
        if (finalTranscript && currentQuestionIdRef.current) {
          const questionId = currentQuestionIdRef.current;
          setAnswers(prev => {
            const currentText = prev[questionId] || '';
            const newText = currentText ? `${currentText} ${finalTranscript}` : finalTranscript;
            console.log('Updating answer for question:', questionId, 'New text:', newText);
            return {
              ...prev,
              [questionId]: newText
            };
          });
        }
      };

      recognitionInstance.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error, event);
        setIsRecording(null);
        currentQuestionIdRef.current = null;
        
        // More specific error messages
        let errorMessage = t('ama.expert.dictationError');
        if (event.error === 'not-allowed') {
          errorMessage = 'Mikrofontilgang nektet. Vennligst gi tillatelse til å bruke mikrofonen.';
        } else if (event.error === 'no-speech') {
          errorMessage = 'Ingen tale oppdaget. Prøv å snakke tydeligere.';
        } else if (event.error === 'network') {
          errorMessage = 'Nettverksfeil. Sjekk internettforbindelsen din.';
        }
        
        toast.error(errorMessage);
      };

      recognitionInstance.onend = () => {
        console.log('Speech recognition ended');
        if (isRecording) {
          // Restart if still recording
          console.log('Restarting recognition...');
          try {
            recognitionInstance.start();
          } catch (error) {
            console.error('Error restarting recognition:', error);
            setIsRecording(null);
            currentQuestionIdRef.current = null;
          }
        }
      };

      setRecognition(recognitionInstance);
    } else {
      console.error('Speech recognition not supported in this browser');
      toast.error('Diktering støttes ikke i denne nettleseren. Prøv Chrome eller Edge.');
    }
  }, [i18n.language, t, isRecording]);

  const startDictation = (questionId: string) => {
    console.log('startDictation called for question:', questionId);
    
    if (!recognition) {
      console.error('Recognition not available');
      toast.error(t('ama.expert.dictationNotSupported'));
      return;
    }

    if (isRecording === questionId) {
      console.log('Stopping dictation');
      try {
        recognition.stop();
      } catch (error) {
        console.error('Error stopping recognition:', error);
      }
      setIsRecording(null);
      currentQuestionIdRef.current = null;
    } else {
      console.log('Starting dictation');
      currentQuestionIdRef.current = questionId;
      setIsRecording(questionId);
      
      try {
        recognition.start();
        toast.success('Diktering startet');
      } catch (error) {
        console.error('Error starting recognition:', error);
        toast.error('Kunne ikke starte diktering. Prøv igjen.');
        setIsRecording(null);
        currentQuestionIdRef.current = null;
      }
    }
  };

  const handlePublish = (questionId: string) => {
    const answerText = answers[questionId];
    if (!answerText?.trim()) {
      toast.error(t('ama.expert.emptyAnswer'));
      return;
    }

    publishAnswerMutation.mutate({ questionId, answerText });
    setAnswers(prev => ({ ...prev, [questionId]: '' }));
  };

  const hasExistingAnswer = (questionId: string) => {
    return existingAnswers?.find(a => a.question_id === questionId);
  };

  const approvedQuestions = questions?.filter(q => q.status === 'approved') || [];

  if (!token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              {t('ama.expert.invalidToken')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!ama) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              {t('common.loading')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MenuBar />
      
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <Badge variant="default" className="mb-2">
                  {t('ama.expert.expertPanel')}
                </Badge>
                <CardTitle className="text-2xl mb-2">{ama.topic}</CardTitle>
                <div className="text-sm text-muted-foreground">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  {format(new Date(ama.scheduled_at), 'PPP', { locale })}
                </div>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Instructions */}
        <Card className="mb-8 bg-muted/50">
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-2">{t('ama.expert.instructions')}</h3>
            <ul className="space-y-1 text-sm text-muted-foreground list-disc list-inside">
              <li>{t('ama.expert.instructionApproved')}</li>
              <li>{t('ama.expert.instructionDictation')}</li>
              <li>{t('ama.expert.instructionPublish')}</li>
              <li>{t('ama.expert.instructionVisible')}</li>
            </ul>
          </CardContent>
        </Card>

        {/* Questions Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {approvedQuestions.map((question: AMAQuestion) => {
            const existingAnswer = hasExistingAnswer(question.id);
            
            return (
              <Card key={question.id} className={existingAnswer ? 'border-green-200' : ''}>
                <CardHeader>
                  <div className="space-y-2">
                    {existingAnswer && (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        {t('ama.expert.published')}
                      </Badge>
                    )}
                    <p className="font-medium">{question.question_text}</p>
                    {question.asker_name && (
                      <p className="text-sm text-muted-foreground">
                        — {question.asker_name}
                      </p>
                    )}
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {existingAnswer ? (
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-2">
                        {t('ama.expert.yourAnswer')}:
                      </p>
                      <p className="whitespace-pre-wrap">{existingAnswer.answer_text}</p>
                    </div>
                  ) : (
                    <>
                      <Textarea
                        value={answers[question.id] || ''}
                        onChange={(e) => setAnswers(prev => ({
                          ...prev,
                          [question.id]: e.target.value
                        }))}
                        placeholder={t('ama.expert.answerPlaceholder')}
                        rows={6}
                        className="resize-none"
                      />
                      
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant={isRecording === question.id ? "destructive" : "outline"}
                          size="sm"
                          onClick={() => startDictation(question.id)}
                          disabled={!recognition}
                        >
                          {isRecording === question.id ? (
                            <>
                              <MicOff className="h-4 w-4 mr-2" />
                              {t('ama.expert.stopDictation')}
                            </>
                          ) : (
                            <>
                              <Mic className="h-4 w-4 mr-2" />
                              {t('ama.expert.startDictation')}
                            </>
                          )}
                        </Button>
                        
                        <Button
                          onClick={() => handlePublish(question.id)}
                          disabled={!answers[question.id]?.trim() || publishAnswerMutation.isPending}
                          size="sm"
                        >
                          <Send className="h-4 w-4 mr-2" />
                          {t('ama.expert.publish')}
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {approvedQuestions.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">
                {t('ama.expert.noQuestions')}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AMAAnswerPage;
